import styled from "@emotion/styled";
import { FilloutStandardEmbed } from "@fillout/react";

import celine from "@/../../hypetrain/static/img/celine.png";
import jeremy from "@/../../hypetrain/static/img/jeremy.png";
import justin from "@/../../hypetrain/static/img/justin.png";
import liz from "@/../../hypetrain/static/img/liz.png";
import niya from "@/../../hypetrain/static/img/niya.png";
import rockwell from "@/../../hypetrain/static/img/rockwell.png";
import { Box, Flex } from "@/components/layout/flexbox";
import { breakpoints, Media } from "@/components/layout/Media";
import { Carousel } from "@/components/pieces/AnimatedCarousel";
import { Container } from "@/components/pieces/bootstrap";
import { FiveStars } from "@/components/pieces/marketing/FiveStars";
import { HEADING_FONT, STANDARD_NAV_HEIGHT } from "@/css/constants";
import { Testimonial } from "@/types/marketing";

const QUOTES: Testimonial[] = [
  {
    quote:
      "It completely blew me away. The ability to create custom scenarios to prepare for tricky conversations is so useful. And the practical, targeted feedback it gives is just gold. I felt like I improved my approach in literally minutes.",
    name: "Rockwell Shah",
    title: "CEO, Ozlo",
    photo: rockwell,
  },
  {
    quote:
      "Roleplays is an incredible tool for developing core communication skills. It’s the exact type of practice that will make you feel confident during critical conversations—and get the results you want.",
    name: "Liz Fosslien",
    title: "Head of Storytelling, Atlassian",
    photo: liz,
  },
  {
    quote:
      "So many problems at companies come down to managers avoiding (or botching) key conversations. Roleplays is the first AI tool I’ve seen that prepares people to do a better job at navigating them.",
    name: "Justin Mares",
    title: "Co-founder, Truemed",
    photo: justin,
  },
  {
    quote:
      "I love this tool. After my first attempt, it was immediately clear to me how this could revolutionize sales enablement for us and prepare our team to navigate challenging calls with prospects. We can’t wait to roll this out org wide.",
    name: "Niya Dragova",
    title: "Founder, Candor",
    photo: niya,
  },
  {
    quote:
      "One of my biggest priorities as a Series B startup CEO is helping my team learn and upscale their people management skills. Roleplays are a great way to do this on demand, at scale",
    name: "Celine Halioua",
    title: "CEO, Loyal",
    photo: celine,
  },
  {
    quote:
      "This thing is amazing. I’ve been using it to create custom scenarios to prepare for hard conversations. In less than 10 minutes of practice, I feel like it helped me improve my approach.",
    name: "Jeremy Fiance",
    title: "Founder, The House Fund",
    photo: jeremy,
  },
];

export function MainHero() {
  const quoteBox = (
    <Box mt={4}>
      <Carousel
        circular={false}
        paginatorButtonStyle={{ backgroundColor: "#FFF" }}
      >
        {QUOTES.map((quote, i) => (
          <Box
            key={`quote-${i}`}
            p={3}
            backgroundColor="rgba(255, 255, 255, 0.5)"
            className="round-corners"
          >
            <FiveStars />
            <Box mt={24}>
              <p>{quote.quote}</p>
              <Flex alignItems="center">
                {quote.photo ? (
                  <Box mr={2}>
                    <img
                      src={quote.photo}
                      alt={quote.name}
                      style={{
                        borderRadius: "100%",
                        height: 50,
                        width: 50,
                      }}
                    />
                  </Box>
                ) : null}
                <Box>
                  <Box fontWeight={600}>{quote.name}</Box>
                  <Box mt={1}>{quote.title}</Box>
                </Box>
              </Flex>
            </Box>
          </Box>
        ))}
      </Carousel>
    </Box>
  );
  return (
    <HeroBG pt={STANDARD_NAV_HEIGHT}>
      <Container className="!max-w-marketing-content">
        <HeroFlex width="100%" mx="auto" alignItems="center" py={70}>
          <Box css={{ flex: 1 }}>
            <Box
              color="#005A9D"
              fontFamily={HEADING_FONT}
              fontSize={48}
              fontWeight={600}
            >
              A Better Way to Train
            </Box>
            <Box mt={2} fontSize={22}>
              Too often training fails to stick. Exec offers a better way to
              prepare employees for company-defining conversations.
            </Box>
            <Media greaterThan="sm">{quoteBox}</Media>
          </Box>
          <DemoBox>
            <Box
              fontWeight={600}
              fontSize={22}
              textAlign="center"
              py={3}
              className="bbs"
            >
              Book Demo
            </Box>
            <Box>
              <FilloutStandardEmbed
                filloutId="nrJWnDcGiSus"
                dynamicResize={true}
                inheritParameters
              />
            </Box>
          </DemoBox>
          <Media at="sm">
            {(className, renderChildren) =>
              renderChildren && (
                <Box width="100%" className={className}>
                  {quoteBox}
                </Box>
              )
            }
          </Media>
        </HeroFlex>
      </Container>
    </HeroBG>
  );
}

const HeroFlex = styled(Flex)`
  gap: 150px;

  @media (max-width: ${breakpoints.lg}px) {
    gap: 50px;
  }

  @media (max-width: ${breakpoints.md}px) {
    padding: 24px;
    flex-direction: column;
  }
`;

const DemoBox = styled(Box)`
  flex: 1;
  background-color: #fff;
  border-radius: 24px;
  border: 16px solid #def0fc;

  @media (max-width: ${breakpoints.md}px) {
    width: 100%;
  }
`;

const HeroBG = styled(Box)`
  background:
    radial-gradient(
      54.56% 38.14% at 32.93% 41.02%,
      rgba(255, 255, 255, 0.02) 0%,
      rgba(255, 255, 255, 0) 100%
    ),
    linear-gradient(93deg, #f2faff 2.27%, #d8ecf8 97.85%);
  background-blend-mode: plus-lighter, normal;
`;
